import { FC, FormEventHandler, useState } from 'react';

import { FieldFooterState } from '@lichtblick/photon/molecules/field-footer/field-footer';

import { LeadFormValues } from './Form.types';

const debounceTimers = new Map<() => void, NodeJS.Timeout>();

export const debounce = (callback: () => void, delay = 500) => {
  clearTimeout(debounceTimers.get(callback));
  debounceTimers.set(callback, setTimeout(callback, delay));
};

export const getFormError = (invalidFields?: NodeListOf<HTMLElement>) => {
  const errorMessages: Record<string, string[]> = {};

  invalidFields?.forEach((field) => {
    const inputName = field.getAttribute('name');
    const statusId = field.getAttribute('aria-describedby')?.split(' ')[0];
    const validationMessage = statusId && field.closest('form')?.querySelector<HTMLElement>(`#${statusId}`)?.innerText;

    if (inputName && validationMessage) {
      errorMessages[validationMessage] ??= [];
      errorMessages[validationMessage].push(inputName);
    }
  });

  return Object.entries(errorMessages)
    .map(([message, fields]) => `> ${message}: ${fields.join(', ')}`)
    .join('\n');
};

export const getFormProduct = (input: LeadFormValues) => {
  let product = 'Power_Gas';

  if (input.energyAnualConsumptionPlan === undefined) {
    product = 'Gas';
  } else if (input.gasAnualConsumptionPlan === undefined) {
    product = 'Power';
  }

  return product;
};

type ErrorMessages = {
  invalid?: string;
  max?: string;
  maxLength?: string;
  min?: string;
  minLength?: string;
  pattern?: string;
  required?: string;
  step?: string;
};

const getErrorMessage = (input: HTMLInputElement, messages?: ErrorMessages) => {
  const { max, maxLength, min, minLength, step, validity } = input;

  if (validity) {
    switch (true) {
      case validity.valid:
        return;
      case validity.patternMismatch:
        return messages?.pattern ?? 'Bitte überprüfen Sie das Format Ihrer Eingabe';
      case validity.rangeOverflow:
        return messages?.max ?? `Der Wert darf nicht größer als ${max} sein`;
      case validity.rangeUnderflow:
        return messages?.min ?? `Der Wert darf nicht kleiner als ${min} sein`;
      case validity.stepMismatch:
        return messages?.step ?? `Der Wert muss ein Vielfaches von ${step} sein`;
      case validity.tooLong:
        return messages?.maxLength ?? `Der Wert darf nicht länger als ${maxLength} Zeichen sein`;
      case validity.tooShort:
        return messages?.minLength ?? `Der Wert darf nicht kürzer als ${minLength} Zeichen sein`;
      case validity.valueMissing:
        return messages?.required ?? 'Hier fehlt eine Angabe';
      default:
        return messages?.invalid ?? 'Bitte überprüfen Sie Ihre Eingabe';
    }
  }
};

type InputProps<E extends HTMLInputElement = HTMLInputElement> = {
  onBlur?: FormEventHandler<E>;
  onInput?: FormEventHandler<E>;
  onInvalid?: FormEventHandler<E>;
  statusLabel?: string;
  statusType?: FieldFooterState;
};

export const withValidation =
  <E extends HTMLInputElement, P>(
    Component: FC<InputProps<E> & P>,
  ): FC<InputProps<E> & P & { messages?: ErrorMessages }> =>
  ({ messages, onBlur, onInput, onInvalid, statusLabel, statusType, ...props }) => {
    const [error, setError] = useState<string>();
    const [isTouched, setIsTouched] = useState(false);

    const validate = (input: EventTarget) => setError(getErrorMessage(input as HTMLInputElement, messages));

    return (
      <Component
        {...(props as P)}
        onBlur={(event) => {
          setIsTouched(true);
          validate(event.target);
          onBlur?.(event);
        }}
        onInput={(event) => {
          if (isTouched || error || (event.target as HTMLInputElement).type === 'checkbox') {
            validate(event.target);
          }

          onInput?.(event);
        }}
        onInvalid={(event) => {
          event.preventDefault();
          validate(event.target);
          onInvalid?.(event);
        }}
        statusLabel={error || statusLabel}
        statusType={error ? 'error' : statusType}
      />
    );
  };
